import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, Loader2, Send, Sparkles, XCircle } from "lucide-react";
import { api, formatApiError, streamChat } from "../lib/api";

const ACTIONS = [
    { id: "sammanfattning", label: "Sammanfattning" },
    { id: "teman", label: "Teman" },
    { id: "swot", label: "SWOT-utkast" },
    { id: "fragor", label: "Frågeförslag" },
    { id: "kapacitet", label: "Kapacitet & värde" },
    { id: "systemanalys", label: "Systemanalys" },
    { id: "helikopter", label: "Helikopteranalys" },
    { id: "diagnos", label: "Diagnos" },
];

function DraftBody({ typ, innehall }) {
    let c;
    try { c = JSON.parse(innehall); } catch { return <p className="text-sm text-slate-300">{innehall}</p>; }
    if (typ === "sammanfattning") return <p className="text-sm text-slate-300 leading-relaxed">{c.text}</p>;
    if (typ === "teman") return (
        <div className="space-y-2">
            {(c.teman || []).map((t, i) => (
                <div key={i} className="rounded-lg bg-abyss/40 border border-border p-2.5">
                    <div className="text-sm font-semibold text-slate-200">{t.titel}</div>
                    <p className="text-xs text-slate-400 mt-0.5">{t.beskrivning}</p>
                </div>
            ))}
        </div>
    );
    if (typ === "swot") {
        const q = [
            { key: "styrkor", label: "Styrkor", color: "#34D399" },
            { key: "svagheter", label: "Svagheter", color: "#F97316" },
            { key: "mojligheter", label: "Möjligheter", color: "#38BDF8" },
            { key: "hot", label: "Hot", color: "#EF4444" },
        ];
        return (
            <div className="grid grid-cols-2 gap-2">
                {q.map(({ key, label, color }) => (
                    <div key={key} className="rounded-lg border p-2.5" style={{ borderColor: `${color}44`, background: `${color}0a` }}>
                        <p className="font-mono text-[9px] uppercase tracking-widest mb-1.5" style={{ color }}>{label}</p>
                        <ul className="space-y-1">
                            {(c[key] || []).map((s, i) => <li key={i} className="text-[11px] text-slate-300 leading-snug">· {s}</li>)}
                        </ul>
                    </div>
                ))}
            </div>
        );
    }
    if (typ === "kapacitet") return (
        <div className="space-y-2">
            <p className="text-sm text-slate-300 leading-relaxed">{c.sammanfattning}</p>
            {(c.avdelningar || []).map((a, i) => {
                const badge = { ja: ["Tid på rätt saker", "#34D399"], delvis: ["Delvis rätt tid", "#F59E0B"], nej: ["Tid på fel saker", "#EF4444"] }[a.tid_pa_ratt_saker] || ["Okänt", "#64748B"];
                return (
                    <div key={i} className="rounded-lg bg-abyss/40 border border-border p-2.5">
                        <div className="flex items-center justify-between gap-2">
                            <span className="text-sm font-semibold text-slate-200">{a.namn}</span>
                            <span className="font-mono text-[9px] uppercase tracking-widest shrink-0" style={{ color: badge[1] }}>{badge[0]}</span>
                        </div>
                        <p className="text-xs text-slate-400 mt-1 leading-relaxed">{a.bedomning}</p>
                        {(a.system_som_hindrar || []).length > 0 && (
                            <p className="text-[11px] text-red-400/90 mt-1.5">Hindrande system: {a.system_som_hindrar.join(" · ")}</p>
                        )}
                    </div>
                );
            })}
            {(c.rekommendationer || []).length > 0 && (
                <div className="pt-1 space-y-1">
                    {(c.rekommendationer || []).map((r, i) => <p key={i} className="text-xs text-slate-400">→ {r}</p>)}
                </div>
            )}
        </div>
    );
    if (typ === "systemanalys") return (
        <div className="space-y-2">
            <p className="text-sm text-slate-300 leading-relaxed">{c.sammanfattning}</p>
            {(c.system || []).map((s, i) => {
                const badge = { ja: ["Bidrar till målen", "#34D399"], delvis: ["Bidrar delvis", "#F59E0B"], nej: ["Bidrar inte", "#EF4444"] }[s.bidrar_till_malen] || ["Okänt", "#64748B"];
                return (
                    <div key={i} className="rounded-lg bg-abyss/40 border border-border p-2.5">
                        <div className="flex items-center justify-between gap-2">
                            <span className="text-sm font-semibold text-slate-200">{s.namn}</span>
                            <span className="font-mono text-[9px] uppercase tracking-widest shrink-0" style={{ color: badge[1] }}>{badge[0]}</span>
                        </div>
                        <p className="text-xs text-slate-400 mt-1 leading-relaxed">{s.bedomning}</p>
                        {s.rekommendation && <p className="text-[11px] text-current mt-1">→ {s.rekommendation}</p>}
                    </div>
                );
            })}
            {(c.overlapp || []).length > 0 && <p className="text-[11px] text-slate-500">Överlapp: {c.overlapp.join(" · ")}</p>}
            {(c.risker || []).map((r, i) => <p key={i} className="text-[11px] text-red-400/90">Risk: {r}</p>)}
        </div>
    );
    if (typ === "helikopter") return (
        <div className="space-y-2">
            <p className="text-sm text-slate-300 leading-relaxed">{c.sammanfattning}</p>
            {(c.malkonflikter || []).map((m, i) => (
                <div key={i} className="rounded-lg bg-abyss/40 border border-amber-500/30 p-2.5">
                    <p className="text-xs font-semibold text-amber-300">{m.mal}</p>
                    <p className="text-xs text-slate-400 mt-1 leading-relaxed">Verklighet: {m.verklighet}</p>
                    {(m.dammar || []).length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1.5">
                            {m.dammar.map((d, j) => <span key={j} className="text-[10px] bg-amber-500/10 text-amber-400 border border-amber-500/30 rounded-full px-2 py-0.5">{d}</span>)}
                        </div>
                    )}
                </div>
            ))}
            {(c.strategier_som_haller || []).map((s, i) => <p key={i} className="text-xs text-buoy">✓ {s}</p>)}
            {(c.strategier_som_bryts || []).map((s, i) => (
                <p key={i} className="text-xs text-red-400/90">✗ {s.strategi} – {s.orsak}</p>
            ))}
            {(c.rekommendationer || []).map((r, i) => <p key={i} className="text-xs text-slate-400">→ {r}</p>)}
        </div>
    );
    if (typ === "diagnos") return (
        <div className="space-y-2">
            <div className="flex items-center gap-3 rounded-lg bg-abyss/40 border border-border p-3">
                <span className="font-mono text-[9px] uppercase tracking-widest text-slate-500">Organisationens hälsa</span>
                <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map((v) => (
                        <span key={v} className="w-5 h-2 rounded-full" style={{ background: v <= (c.halsa || 0) ? (c.halsa <= 2 ? "#EF4444" : c.halsa <= 3 ? "#F59E0B" : "#34D399") : "#1E293B" }} />
                    ))}
                </div>
                <span className="font-mono text-xs text-slate-300">{c.halsa}/5</span>
            </div>
            <p className="text-sm text-slate-300 leading-relaxed">{c.diagnos}</p>
            {(c.symptom || []).length > 0 && (
                <div>
                    <p className="font-mono text-[9px] uppercase tracking-widest text-slate-500 mb-1">Symptom</p>
                    {(c.symptom || []).map((s, i) => <p key={i} className="text-xs text-slate-400">· {s}</p>)}
                </div>
            )}
            {(c.orsaker || []).length > 0 && (
                <div>
                    <p className="font-mono text-[9px] uppercase tracking-widest text-slate-500 mb-1">Bakomliggande orsaker</p>
                    {(c.orsaker || []).map((s, i) => <p key={i} className="text-xs text-red-400/90">· {s}</p>)}
                </div>
            )}
            {(c.ordination || []).length > 0 && (
                <div>
                    <p className="font-mono text-[9px] uppercase tracking-widest text-buoy mb-1">Ordination</p>
                    {(c.ordination || []).map((s, i) => <p key={i} className="text-xs text-slate-300">{i + 1}. {s}</p>)}
                </div>
            )}
        </div>
    );
    if (typ === "enkatsammanstallning") return (
        <div className="space-y-2">
            <p className="text-sm text-slate-300 leading-relaxed">{c.sammanfattning}</p>
            <div className="flex flex-wrap gap-1">
                {(c.teman || []).map((t, i) => (
                    <span key={i} className="text-[10px] bg-current/10 text-current border border-current/30 rounded-full px-2 py-0.5">
                        {t.titel || t}
                    </span>
                ))}
            </div>
            {(c.svar_per_fraga || []).map((f, i) => (
                <div key={i} className="rounded-lg bg-abyss/40 border border-border p-2.5">
                    <p className="text-xs font-semibold text-slate-200">{f.fraga}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{f.sammanfattning}</p>
                </div>
            ))}
        </div>
    );
    if (typ === "fragor") return (
        <ol className="space-y-1.5 list-decimal list-inside">
            {(c.fragor || []).map((f, i) => <li key={i} className="text-sm text-slate-300 leading-snug">{f}</li>)}
        </ol>
    );
    return <pre className="text-xs text-slate-400 whitespace-pre-wrap">{innehall}</pre>;
}

const STATUS_LABEL = { utkast: "AI-utkast – kräver granskning", godkand: "Godkänd av konsult", avvisad: "Avvisad" };
const TYPE_LABELS = { sammanfattning: "Sammanfattning", teman: "Teman", swot: "SWOT-utkast", fragor: "Frågeförslag", enkatsammanstallning: "Enkätsammanställning", sammanstallning: "Sammanställning", kapacitet: "Kapacitet & värde", systemanalys: "Systemanalys", helikopter: "Helikopteranalys", diagnos: "Organisatorisk diagnos", presentation: "Presentationsutkast", strukturforslag: "Strukturförslag" };
const typeLabel = (t) => TYPE_LABELS[t] || t;

export default function AiPanel({ projectId, drafts, refresh }) {
    const [loading, setLoading] = useState(null);
    const [selected, setSelected] = useState(null);
    const [suggesting, setSuggesting] = useState(false);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [streaming, setStreaming] = useState(false);
    const chatEndRef = useRef(null);

    const active = selected ? drafts.find((d) => d.id === selected) : drafts[0];

    useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

    const generate = async (typ) => {
        setLoading(typ);
        try {
            const r = await api.post(`/projects/${projectId}/ai/draft`, { typ });
            await refresh();
            setSelected(r.data.id);
        } catch (e) {
            toast.error(formatApiError(e, "AI-genereringen misslyckades"));
        } finally {
            setLoading(null);
        }
    };

    const setStatus = async (did, status) => {
        await api.put(`/drafts/${did}`, { status });
        toast.success(status === "godkand" ? "Utkast godkänt" : "Utkast avvisat");
        refresh();
    };

    const toSource = async (did) => {
        try {
            await api.post(`/drafts/${did}/to-source`);
            toast.success("Källa sparad i empirin");
            refresh();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const suggest = async () => {
        setSuggesting(true);
        try {
            const r = await api.post(`/projects/${projectId}/bottlenecks/suggest`);
            toast.success(r.data.skapade.length ? `${r.data.skapade.length} dammförslag skapade – granska dem i kartan` : "Inga nya dammförslag");
            refresh();
        } catch (e) {
            toast.error(formatApiError(e, "Förslagsgenereringen misslyckades"));
        } finally {
            setSuggesting(false);
        }
    };

    const send = async () => {
        const msg = input.trim();
        if (!msg || streaming) return;
        setInput("");
        const history = [...messages, { role: "user", content: msg }];
        setMessages([...history, { role: "assistant", content: "" }]);
        setStreaming(true);
        try {
            await streamChat(projectId, msg, messages, (delta) => {
                setMessages((prev) => {
                    const next = [...prev];
                    next[next.length - 1] = { role: "assistant", content: next[next.length - 1].content + delta };
                    return next;
                });
            });
        } catch (e) {
            toast.error("Chatten misslyckades – försök igen");
        } finally {
            setStreaming(false);
        }
    };

    return (
        <div className="flex-1 flex flex-col min-h-0" data-testid="ai-panel">
            <div className="p-4 border-b border-border space-y-3">
                <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">AI tar ytan – du tar djupet</p>
                <div className="grid grid-cols-2 gap-1.5">
                    {ACTIONS.map((a) => (
                        <button key={a.id} data-testid={`ai-draft-${a.id}`} onClick={() => generate(a.id)} disabled={!!loading}
                            className="flex items-center gap-1.5 rounded-lg border border-aidraft/30 bg-aidraft/5 text-aidraft px-2.5 py-2 text-xs font-medium hover:bg-aidraft/15 transition-colors disabled:opacity-50">
                            {loading === a.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                            {a.label}
                        </button>
                    ))}
                </div>
                <button data-testid="ai-suggest-button" onClick={suggest} disabled={suggesting}
                    className="w-full flex items-center justify-center gap-2 rounded-lg border border-amber-500/40 text-amber-400 px-3 py-2 text-xs font-semibold hover:bg-amber-500/10 transition-colors disabled:opacity-50">
                    {suggesting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <AlertTriangle className="w-3.5 h-3.5" />}
                    Föreslå misstänkta dammar
                </button>
            </div>

            <div className="flex-1 overflow-y-auto panel-scroll p-4 space-y-4">
                {active && (
                    <div data-testid="ai-draft-card" className="rounded-xl border border-aidraft/30 bg-aidraft/5 p-3.5">
                        <div className="flex items-center justify-between mb-2">
                            <span data-testid="ai-draft-badge" className={`font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${active.status === "utkast" ? "text-aidraft border-aidraft/40 bg-aidraft/10" : active.status === "godkand" ? "text-buoy border-buoy/40 bg-buoy/10" : "text-slate-500 border-slate-600"}`}>
                                {STATUS_LABEL[active.status]}
                            </span>
                            <span className="font-mono text-[9px] text-slate-600">{typeLabel(active.typ)}</span>
                        </div>
                        <DraftBody typ={active.typ} innehall={active.innehall} />
                        {active.status === "utkast" && (
                            <div className="flex gap-2 mt-3">
                                <button data-testid="ai-draft-approve" onClick={() => setStatus(active.id, "godkand")}
                                    className="flex-1 flex items-center justify-center gap-1.5 rounded-lg bg-buoy/15 border border-buoy/40 text-buoy py-1.5 text-xs font-semibold hover:bg-buoy/25 transition-colors">
                                    <CheckCircle2 className="w-3.5 h-3.5" /> Godkänn
                                </button>
                                <button data-testid="ai-draft-reject" onClick={() => setStatus(active.id, "avvisad")}
                                    className="flex-1 flex items-center justify-center gap-1.5 rounded-lg border border-slate-600 text-slate-400 py-1.5 text-xs hover:border-red-500/50 hover:text-red-400 transition-colors">
                                    <XCircle className="w-3.5 h-3.5" /> Avvisa
                                </button>
                            </div>
                        )}
                        {active.typ === "enkatsammanstallning" && active.status === "godkand" && (
                            <button data-testid="draft-to-source-button" onClick={() => toSource(active.id)}
                                className="w-full mt-3 flex items-center justify-center gap-1.5 rounded-lg bg-current/15 border border-current/40 text-current py-1.5 text-xs font-semibold hover:bg-current/25 transition-colors">
                                Spara som källa i empirin
                            </button>
                        )}
                    </div>
                )}
                {drafts.length > 1 && (
                    <select data-testid="ai-draft-history" className="w-full bg-abyss/60 border border-border rounded-lg px-2.5 py-1.5 text-xs text-slate-400 outline-none"
                        value={active?.id || ""} onChange={(e) => setSelected(e.target.value)}>
                        {drafts.map((d) => (
                            <option key={d.id} value={d.id}>
                                {`${typeLabel(d.typ)} · ${new Date(d.created_at).toLocaleString("sv-SE")} · ${STATUS_LABEL[d.status]}`}
                            </option>
                        ))}
                    </select>
                )}

                <div className="space-y-3">
                    {messages.map((m, i) => (
                        <div key={i} data-testid={`chat-message-${i}`} className={`rounded-xl px-3 py-2 text-sm leading-relaxed ${m.role === "user" ? "bg-current/10 border border-current/20 text-slate-200 ml-6" : "bg-deepsea/60 border border-border text-slate-300 mr-6"}`}>
                            {m.content || (streaming && i === messages.length - 1 ? <span className="text-slate-600">tänker …</span> : "")}
                        </div>
                    ))}
                    <div ref={chatEndRef} />
                </div>
            </div>

            <div className="p-3 border-t border-border">
                <div className="flex gap-2">
                    <input
                        data-testid="ai-chat-input"
                        className="flex-1 bg-abyss/60 border border-border rounded-lg px-3 py-2 text-sm text-slate-100 outline-none focus:border-current/60 transition-colors"
                        placeholder="Fråga om kartan, dammarna, empirin …"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && send()}
                    />
                    <button data-testid="ai-chat-send" onClick={send} disabled={streaming || !input.trim()}
                        className="shrink-0 bg-current text-abyss rounded-lg px-3 hover:bg-tide transition-colors disabled:opacity-40">
                        <Send className="w-4 h-4" />
                    </button>
                </div>
            </div>
        </div>
    );
}
